import type { DraftState } from '../types';
import { maxBidPower, nextBidderId } from '../lib/draftLogic';
import { TEAMS_BY_ABBR } from '../constants/teams';

interface Props {
  state: DraftState;
  localManagerId: string | null;
}

/** Sticky strip showing each manager's $ left, max bid, and who's on the clock. */
export function BiddingIntelStrip({ state, localManagerId }: Props) {
  const activeBidder = state.phase === 'Bidding' ? nextBidderId(state) : null;
  const nominee = state.phase === 'Nomination' ? state.managers[state.currentNomineeIndex]?.id : null;
  const auction = state.auction;
  const teamMeta = auction.team ? TEAMS_BY_ABBR[auction.team] : null;

  return (
    <div className="bg-slate-900/80 backdrop-blur border-y border-slate-700 -mx-4 md:-mx-6 px-4 md:px-6 py-2 mb-4 sticky top-0 z-30">
      <div className="flex items-center gap-3 overflow-x-auto">
        {/* Live auction summary on the left */}
        {state.phase === 'Bidding' && teamMeta ? (
          <div className="flex items-center gap-2 pr-3 border-r border-slate-700 shrink-0">
            <span className="pill bg-rose-600 text-white text-[9px] animate-pulse">LIVE</span>
            <div className="text-xs">
              <span className="text-white font-bold">{teamMeta.abbr}</span>
              <span className="text-slate-400"> @ </span>
              <span className="text-rose-300 font-extrabold">${auction.highBid}</span>
            </div>
          </div>
        ) : state.phase === 'Nomination' ? (
          <div className="text-[10px] uppercase text-slate-400 pr-3 border-r border-slate-700 shrink-0">
            Awaiting nomination
          </div>
        ) : (
          <div className="text-[10px] uppercase text-emerald-400 pr-3 border-r border-slate-700 shrink-0">
            Draft complete
          </div>
        )}

        {/* Per-manager cards */}
        <div className="flex gap-2 overflow-x-auto flex-1">
          {state.managers.map(m => {
            const power = maxBidPower(state, m.id);
            const slotsLeft = state.rosterCap - m.teamsDrafted;
            const isYou = m.id === localManagerId;
            const isActive = m.id === activeBidder || m.id === nominee;
            const isHigh = m.id === auction.highBidderId;
            const isPassed = auction.passers.includes(m.id);
            const isSqueezed = slotsLeft > 0 && (power <= 3 || (slotsLeft > 1 && m.budget / slotsLeft <= 2));
            return (
              <div
                key={m.id}
                className={`flex items-center gap-2 px-2.5 py-1 rounded-lg border whitespace-nowrap text-xs transition ${
                  isActive
                    ? 'bg-amber-900/40 border-amber-400 shadow-sm shadow-amber-400/20'
                    : isHigh
                    ? 'bg-rose-900/30 border-rose-500 shadow-sm shadow-rose-500/20'
                    : isPassed
                    ? 'bg-slate-800/40 border-slate-700 opacity-50'
                    : 'bg-slate-800/60 border-slate-700'
                }`}
                title={isPassed ? 'Passed on this auction' : isSqueezed ? 'Cap-squeezed! Limited bidding power' : ''}
              >
                <span className="text-base">{m.emoji}</span>
                <div className="flex flex-col leading-tight">
                  <span className="font-bold text-white flex items-center gap-1">
                    {m.name}{isYou && <span className="text-teal-400">(you)</span>}
                    {m.id === 'rob' && <span className="text-[9px] text-amber-300 font-normal">👑</span>}
                  </span>
                  <span className="text-[10px] text-slate-400">
                    <span className="text-emerald-400 font-semibold">${m.budget}</span>
                    {' · max '}
                    <span className={`font-semibold ${isSqueezed ? 'text-rose-400' : 'text-teal-300'}`}>${power}</span>
                    {' · '}
                    {slotsLeft} slot{slotsLeft === 1 ? '' : 's'}
                  </span>
                </div>
                {isHigh && <span className="pill bg-rose-500 text-white text-[8px] ml-1">HIGH</span>}
                {isActive && state.phase === 'Bidding' && <span className="pill bg-amber-400 text-slate-950 text-[8px] ml-1 font-black">CLOCK</span>}
                {isActive && state.phase === 'Nomination' && <span className="pill bg-amber-400 text-slate-950 text-[8px] ml-1 font-black">NOM</span>}
                {isSqueezed && !isPassed && <span className="pill bg-rose-900/80 text-rose-300 border border-rose-500/40 text-[8px] ml-1">SQUEEZE</span>}
                {isPassed && <span className="pill bg-slate-600 text-slate-300 text-[8px] ml-1">OUT</span>}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
